import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { createContext } from 'react';
// route change handlers are outside the router, so they can be used in any component!
const changeHandlers = {};
let routersActive = 0;
export const RouterContext = createContext({});
// guards are outside the router, so they can be used in any component
const navigationGuards = {};
export function registerNavigationGuard(id, guard) {
    navigationGuards[id] = guard;
}
export function unregisterNavigationGuard(id) {
    delete navigationGuards[id];
}
/**
 * Asks all guards whether navigating to the target is allowed right now.
 * Returns false if one of them blocked it; that guard got the proceed() callback
 * and may run the navigation later.
 * Use this for actions that navigate as a side effect (log out, switch context),
 * so the action itself is only executed when leaving is allowed.
 */
export function mayNavigate(target, proceed) {
    for (const guard of Object.values(navigationGuards))
        if (!guard(target, proceed))
            return false;
    return true;
}
/** Registers a navigation guard for the lifetime of the component. */
export function useNavigationGuard(id, guard, enabled = true) {
    React.useEffect(() => {
        if (!enabled)
            return;
        registerNavigationGuard(id, guard);
        return () => unregisterNavigationGuard(id);
    }, [id, guard, enabled]);
}
function navigate(ctx, target, mode) {
    history[`${mode == 'silent' ? 'replace' : mode}State`](null, '', target);
    window.setTimeout(() => { var _a; return (_a = ctx.setPath) === null || _a === void 0 ? void 0 : _a.call(ctx, window.location.pathname + window.location.search + window.location.hash, mode == 'silent'); }, 0);
}
// Navigates to the target. Returns false if a navigation guard blocked it.
export function goTo(ctx, target, mode = 'push') {
    if (target === ctx.path)
        return true;
    if (target.startsWith('http') || target.startsWith('//'))
        throw new Error('Link target must be same origin');
    if (!mayNavigate(target, () => navigate(ctx, target, mode)))
        return false;
    navigate(ctx, target, mode);
    return true;
}
// use this to create a function that can be used as an onClick handler for a link
export function useLinkHandler(target, mode = 'push', ctx) {
    const c = ctx !== null && ctx !== void 0 ? ctx : React.useContext(RouterContext);
    return () => {
        goTo(c, target, mode);
        return true;
    };
}
// returns an empty fragment, but navigates to the target.
export function redirect(ctx, target) {
    goTo(ctx, target, 'push');
    return _jsx(_Fragment, {});
}
export function getLocation(routerCtx) {
    const { path } = routerCtx;
    const search = (path === null || path === void 0 ? void 0 : path.replace(/[^?]*/, '').replace(/#.*$/, '')) || '';
    return {
        pathName: (path === null || path === void 0 ? void 0 : path.replace(/[\?#].*$/, '')) || '/',
        search,
        hash: (path === null || path === void 0 ? void 0 : path.replace(/^[^#]*/, '')) || '',
        searchParams: new URLSearchParams(search)
    };
}
export function useLocation() {
    return getLocation(React.useContext(RouterContext));
}
export function useWillLeave(id, handler, routerCtx) {
    const ctx = routerCtx !== null && routerCtx !== void 0 ? routerCtx : React.useContext(RouterContext);
    React.useEffect(() => {
        ctx.registerChangeHandler(id, handler);
        return () => ctx.unregisterChangeHandler(id);
    }, [handler, id]);
}
/**
 * Router component that provides the current path and allows changing it.
 * Use <Routes> to display different content depending on the path.
 */
const Router = ({ children }) => {
    const [path, setPath] = React.useState(window.location.pathname + window.location.search + window.location.hash);
    const pathRef = React.useRef(path); // for the popstate handler, which is installed only once
    pathRef.current = path;
    function changePath(newPath, noEvents = false) {
        if (!noEvents)
            for (const handler of Object.values(changeHandlers))
                handler(newPath);
        setPath(newPath);
    }
    function registerChangeHandler(id, handler) {
        changeHandlers[id] = handler;
    }
    function unregisterChangeHandler(id) {
        delete changeHandlers[id];
    }
    React.useEffect(() => {
        if (routersActive++ > 0)
            throw new Error('Only one Router component can be active at a time');
        window.onpopstate = () => {
            const newPath = window.location.pathname + window.location.search + window.location.hash;
            // A blocked back/forward has already changed the URL: put the old one back,
            // and let the guard replay the jump if the user allows it after all.
            if (!mayNavigate(newPath, () => { history.replaceState(null, '', newPath); changePath(newPath); })) {
                history.pushState(null, '', pathRef.current);
                return;
            }
            changePath(newPath);
        };
        return () => {
            routersActive--;
            window.onpopstate = null;
            for (const id in changeHandlers)
                delete changeHandlers[id];
        };
    }, []);
    return _jsx(_Fragment, { children: _jsx(RouterContext.Provider, { value: { path, setPath: changePath, registerChangeHandler, unregisterChangeHandler }, children: children }) });
};
export default Router;
